import React, { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export type RevealVariant = 'fade-up' | 'fade-down' | 'fade-left' | 'fade-right' | 'fade' | 'scale' | 'blur-up';

export interface ScrollRevealProps {
    children: ReactNode;
    variant?: RevealVariant;
    delay?: number;
    duration?: number;
    distance?: number;
    className?: string;
    once?: boolean;
    threshold?: number;
    as?: keyof React.JSX.IntrinsicElements;
}

const TRANSFORMS: Record<RevealVariant, (d: number) => string> = {
    'fade-up': (d) => `translateY(${d}px)`,
    'fade-down': (d) => `translateY(${-d}px)`,
    'fade-left': (d) => `translateX(${d}px)`,
    'fade-right': (d) => `translateX(${-d}px)`,
    fade: () => 'none',
    scale: () => 'scale(0.92)',
    'blur-up': (d) => `translateY(${d}px)`,
};

/**
 * True when the page is being snapshotted by the prerender step.
 *
 * Prerendering exists to give crawlers real HTML, so the reveal animation must not run — otherwise
 * the snapshot captures `opacity: 0` and we would ship hidden content, which is worse than shipping
 * none. Evaluated once at module load; the flag never appears in the emitted HTML.
 */
const IS_PRERENDER =
    typeof window !== 'undefined' &&
    (window.location.search.includes('__prerender') ||
        (window as unknown as { __PODUS_PRERENDER__?: boolean }).__PODUS_PRERENDER__ === true);

/**
 * Scroll-triggered reveal animation component.
 * Works with custom scroll containers (not just window).
 * By default, replays the animation each time the element enters the viewport.
 *
 * Progressive enhancement: the first render is fully visible, so the content exists for crawlers and
 * for users without JS. The hidden starting state is applied in a layout effect — before the browser
 * paints — so animated behaviour is unchanged once JS runs.
 */
export default function ScrollReveal({
    children,
    variant = 'fade-up',
    delay = 0,
    duration = 700,
    distance = 40,
    className = '',
    once = false,
    threshold = 0.15,
    as: Tag = 'div',
}: ScrollRevealProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    // Until this is armed the element renders in its final, visible state. Arming happens in a
    // layout effect so the switch to the hidden state lands before the first paint — no flash.
    const [armed, setArmed] = useState(false);
    useLayoutEffect(() => {
        if (IS_PRERENDER) return;
        setArmed(true);
    }, []);

    useEffect(() => {
        if (IS_PRERENDER) return;
        const el = ref.current;
        if (!el) return;

        // Find nearest scrollable ancestor for the root
        let root: HTMLElement | null = null;
        let parent = el.parentElement;
        while (parent) {
            const { overflowY } = getComputedStyle(parent);
            if (overflowY === 'auto' || overflowY === 'scroll') {
                root = parent;
                break;
            }
            parent = parent.parentElement;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting) {
                    setVisible(true);
                    if (once) observer.disconnect();
                } else if (entry && !once) {
                    setVisible(false);
                }
            },
            { root, threshold },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [once, threshold]);

    const hidden: React.CSSProperties = {
        opacity: 0,
        transform: TRANSFORMS[variant](distance),
        filter: variant === 'blur-up' ? 'blur(6px)' : undefined,
    };

    const shown: React.CSSProperties = {
        opacity: 1,
        transform: 'translateY(0) translateX(0) scale(1)',
        filter: 'blur(0px)',
    };

    const style: React.CSSProperties = {
        // Before arming (server/prerender/no-JS) the element is shown, so the content is real.
        ...(!armed || visible ? shown : hidden),
        transition: [
            `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
            `transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
            variant === 'blur-up' ? `filter ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms` : '',
        ]
            .filter(Boolean)
            .join(', '),
        willChange: 'opacity, transform',
    };

    return (
        // @ts-expect-error dynamic tag
        <Tag ref={ref} className={className} style={style}>
            {children}
        </Tag>
    );
}

export interface ScrollRevealGroupProps {
    children: ReactNode[];
    variant?: RevealVariant;
    stagger?: number;
    baseDelay?: number;
    className?: string;
    duration?: number;
    distance?: number;
    threshold?: number;
}

/**
 * Convenience wrapper for staggering multiple children.
 * Each direct child gets an incremental delay.
 */
export function ScrollRevealGroup({
    children,
    variant = 'fade-up',
    stagger = 100,
    baseDelay = 0,
    className = '',
    duration,
    distance,
    threshold,
}: ScrollRevealGroupProps) {
    return (
        <>
            {(children as ReactNode[]).map((child, i) => (
                <ScrollReveal
                    key={i}
                    variant={variant}
                    delay={baseDelay + i * stagger}
                    className={className}
                    {...(duration !== undefined && { duration })}
                    {...(distance !== undefined && { distance })}
                    {...(threshold !== undefined && { threshold })}
                >
                    {child}
                </ScrollReveal>
            ))}
        </>
    );
}
